import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  memo,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import { Tree } from 'syngenta-digital-cropwise-react-ui-kit';
import LoadingSpinner from 'components/LoadingSpinner';
import { hasRoleAccess, ROLES } from 'utilities/access';
import { findGeoJSON } from 'screens/Property/helpers/propertyDataHelpers';
import { PropertyTypes } from 'helpers/propertyHelpers';
import { COLLAPSE_PROPERTY, EXPAND_PROPERTIES } from 'reducers/reducer';
import createAction from 'helpers/createAction';
import PropertyLandingContext from 'screens/Property/PropertiesLanding/context';
import FarmRow from '../../../FarmRow';
import FieldRow from '../../../FieldRow';
import CropZoneRow from '../../../CropZoneRow';
import UnarchivePropertyModal from '../../../../UnarchivePropertyModal';
import './index.css';

const buildCropZoneNode = (cropZone, field, farm, options) => {
  const {
    onPropertySelection,
    fieldsAndAreasGeoJSONCollection,
    hasFullControl
  } = options;
  const cropZoneGeoJSON = findGeoJSON(
    fieldsAndAreasGeoJSONCollection,
    cropZone
  );
  const cropZoneWithFieldIdAndFarmId = {
    ...cropZone,
    fieldId: field.id,
    farmId: farm.id,
    archived: field.archived
  };

  return {
    title: (
      <CropZoneRow
        onSelect={onPropertySelection}
        cropZone={cropZoneWithFieldIdAndFarmId}
        icon={cropZoneGeoJSON}
        fieldId={field.id}
        isEditable={hasFullControl && !field.archived}
        parentPage={farm?.parentPage}
      />
    ),
    key: cropZone.id
  };
};

const buildFieldNode = (field, farm, options) => {
  const {
    onPropertySelection,
    fieldsAndAreasGeoJSONCollection,
    hasFullControl,
    onUnarchiveProperty
  } = options;
  const fieldGeoJSON = findGeoJSON(fieldsAndAreasGeoJSONCollection, field);
  const fieldWithFarmId = { ...field, farmId: farm.id };

  return {
    title: (
      <FieldRow
        field={fieldWithFarmId}
        onSelect={onPropertySelection}
        icon={fieldGeoJSON}
        hasEditAccess={!field.archived && hasFullControl}
        showUnarchiveButton={field.archived && hasFullControl}
        onUnarchiveProperty={onUnarchiveProperty}
        parentPage={farm?.parentPage}
      />
    ),
    key: field.id,
    children: field.cropzones.map(cropZone =>
      buildCropZoneNode(cropZone, field, farm, options)
    )
  };
};

const buildFarmNode = (farm, options) => {
  const { onPropertySelection, hasFullControl, onUnarchiveProperty } = options;
  return {
    title: (
      <FarmRow
        farm={farm}
        onSelect={onPropertySelection}
        hasEditAccess={!farm.archived && hasFullControl}
        showUnarchiveButton={farm.archived && hasFullControl}
        onUnarchiveProperty={onUnarchiveProperty}
      />
    ),
    key: farm.id,
    children: farm.fields.map(field => buildFieldNode(field, farm, options))
  };
};

const defaultTreeDefaultPropProperties = [];
const DefaultTree = ({
  properties = defaultTreeDefaultPropProperties,
  propertiesFilter,
  selectedPropertyId = null,
  expandedPropertyIds,
  loggedInUserOrgPermission,
  searchTextValueFFT = '',
  dispatch
}) => {
  const {
    pagination,
    isHugeOrganization,
    isLoadingPaginatedProperties,
    params,
    setParams,
    isLoadingFields,
    fetchProperties,
    onPropertySelection,
    fieldsAndAreasGeoJSONCollection
  } = useContext(PropertyLandingContext);

  const { role } = loggedInUserOrgPermission;
  const [unarchivePropertyModalOpen, setUnarchivePropertyModalOpen] = useState(
    false
  );

  const closeUnarchivePropertyModal = useCallback(() => {
    return setUnarchivePropertyModalOpen(false);
  }, []);

  const [loadingPosition, setLoadingPosition] = useState('bottom');
  const [unarchivedProperty, setUnarchivedProperty] = useState({});
  const [unarchivedPropertyType, setUnarchivedPropertyType] = useState(
    PropertyTypes.FARM
  );

  const lastScrollTop = useRef(0);
  const scrollPosition = useRef(0);
  const containerRef = useRef(null);
  const previousScrollHeight = useRef(0);
  const observerRef = useRef(null);
  const [treeHeight, setTreeHeight] = useState(400);

  // Measure the container height for virtual scrolling
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(([entry]) => {
      const h = entry.contentRect.height;
      if (h > 0) setTreeHeight(h);
    });
    ro.observe(container);
    // eslint-disable-next-line consistent-return
    return () => ro.disconnect();
  }, []);

  const isLoadingPaginatedPropertiesRef = useRef(isLoadingPaginatedProperties);
  isLoadingPaginatedPropertiesRef.current = isLoadingPaginatedProperties;

  const isLoadingFieldsRef = useRef(isLoadingFields);
  isLoadingFieldsRef.current = isLoadingFields;

  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const loadingPositionRef = useRef(loadingPosition);
  loadingPositionRef.current = loadingPosition;

  const handleScroll = useCallback(
    event => {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      scrollPosition.current = scrollTop;

      const isLoading =
        isLoadingPaginatedPropertiesRef.current || isLoadingFieldsRef.current;
      const isScrollingDown = scrollTop + clientHeight >= scrollHeight - 120;
      const isScrollingUp = scrollTop === 0;

      const scrollUpToPosition = () => {
        const container = document.querySelector('[data-tree]');
        if (container) container.scrollTop = 400;
      };

      const lazyLoadData = (page, position) => {
        previousScrollHeight.current = scrollHeight;
        setParams(prev => ({ ...prev, page }));
        setLoadingPosition(position);
      };

      const currentPagination = paginationRef.current;
      if (isScrollingDown && !isLoading) {
        const currPage = currentPagination?.preLoadedPages?.length
          ? Math.max(...currentPagination.preLoadedPages)
          : 0;
        lazyLoadData(currPage + 1, 'bottom');
      }

      const canScrollUp =
        isScrollingUp &&
        (!isLoading || loadingPositionRef.current === 'bottom');
      if (canScrollUp) {
        const currPage = currentPagination?.preLoadedPages?.length
          ? Math.min(...currentPagination.preLoadedPages)
          : 0;
        if (currPage > 0) {
          scrollUpToPosition();
          lazyLoadData(currPage - 1, 'top');
        }
      }
      lastScrollTop.current = scrollTop;
    },
    [setParams]
  );

  useEffect(() => {
    const container = document.querySelector('[data-tree]');
    if (container && isHugeOrganization) {
      container.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (container && isHugeOrganization) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll, isHugeOrganization]);

  useEffect(() => {
    if (!isHugeOrganization) return;
    const container = containerRef.current;
    if (container && previousScrollHeight.current) {
      if (loadingPosition === 'bottom') {
        const scrollHeightDifference =
          container.scrollHeight - previousScrollHeight.current;
        container.scrollTop += scrollHeightDifference;
      }
      previousScrollHeight.current = 0;
    }
  }, [
    params.page,
    isLoadingPaginatedProperties,
    isLoadingFields,
    loadingPosition,
    isHugeOrganization
  ]);

  /**
   * There's an edge case where most properties in the first page are archived, resulting in
   * only a few properties being displayed and preventing the scroll event from triggering further data loading.
   * The logic below ensures that additional data is loaded initially until scrolling becomes possible.
   */
  useEffect(() => {
    if (
      !isHugeOrganization ||
      isLoadingPaginatedProperties ||
      isLoadingFields ||
      !properties?.length ||
      !!searchTextValueFFT?.valueInput // is on search
    ) {
      return;
    }

    let debounceTimeout;
    let shouldCheck = true; // to stop running (disconect observer) when we have enough data already

    const checkIfScrollIsPossible = () => {
      const container = containerRef.current;
      if (!container) return;

      const { scrollHeight, clientHeight } = container;
      const isNotScrollable = scrollHeight <= clientHeight;
      if (isNotScrollable && shouldCheck) {
        const currentPagination = paginationRef.current;
        const lastPageLoaded = currentPagination?.preLoadedPages?.length
          ? Math.max(...currentPagination.preLoadedPages)
          : 0;
        setParams(prev => ({ ...prev, page: lastPageLoaded + 1 }));
        return;
      }

      shouldCheck = false;
      if (observerRef.current && containerRef.current) {
        observerRef.current.disconnect();
      }
    };

    debounceTimeout = setTimeout(checkIfScrollIsPossible, 500);

    observerRef.current = new MutationObserver(() => {
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(checkIfScrollIsPossible, 500);
    });

    if (containerRef?.current) {
      observerRef.current.observe(containerRef.current, {
        childList: true,
        subtree: true
      });
    }

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(debounceTimeout);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (observerRef?.current && containerRef?.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    properties?.length,
    isHugeOrganization,
    isLoadingFields,
    isLoadingPaginatedProperties,
    setParams,
    searchTextValueFFT?.valueInput
  ]);

  const hasFullControl = useMemo(
    () => hasRoleAccess(role, ROLES.FULL_CONTROL),
    [role]
  );

  const selectedKeys = useMemo(
    () => (selectedPropertyId ? [selectedPropertyId] : []),
    [selectedPropertyId]
  );

  const expandOrCollapse = useCallback((isExpand, key) => {
    createAction(
      dispatch,
      isExpand ? EXPAND_PROPERTIES : COLLAPSE_PROPERTY,
      key
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelect = useCallback(
    (newSelectedKeys, info) => {
      const selectedKey = newSelectedKeys[0] ?? info.node.key;
      expandOrCollapse(!!newSelectedKeys[0], selectedKey);
    },
    [expandOrCollapse]
  );

  const onExpand = useCallback(
    (_, { expanded, node }) => {
      expandOrCollapse(expanded, node.key);
    },
    [expandOrCollapse]
  );

  const onUnarchiveProperty = useCallback(property => {
    setUnarchivedProperty(property);
    setUnarchivedPropertyType(
      property.fields ? PropertyTypes.FARM : PropertyTypes.FIELD
    );
    setUnarchivePropertyModalOpen(true);
  }, []);

  const treeData = useMemo(
    () => {
      const options = {
        onPropertySelection,
        onUnarchiveProperty,
        fieldsAndAreasGeoJSONCollection,
        hasFullControl
      };
      return propertiesFilter(properties, searchTextValueFFT)
        .sort((a, b) => a.parentPage - b.parentPage)
        .map(farm => buildFarmNode(farm, options));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      properties,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      searchTextValueFFT?.valueInput,
      propertiesFilter,
      onPropertySelection,
      hasFullControl,
      onUnarchiveProperty,
      fieldsAndAreasGeoJSONCollection
    ]
  );

  return (
    <>
      {isLoadingPaginatedProperties && loadingPosition === 'top' && (
        <div
          data-testid="top-loading"
          className="bg-neutral-100 border-b border-t border-neutral-20 py-4 flex items-center justify-center gap-2"
        >
          <div className="w-30px">
            <LoadingSpinner message="" size={25} inline label="Loading farms" />
          </div>
          <p className="test-sm">Loading previous farms...</p>
        </div>
      )}
      <div
        ref={containerRef}
        data-tree
        className="default-fft h-full overflow-auto"
      >
        <Tree
          selectedKeys={selectedKeys}
          treeData={treeData}
          onSelect={onSelect}
          onExpand={onExpand}
          expandedKeys={expandedPropertyIds}
          virtual
          height={treeHeight}
          itemHeight={58}
          motion={null}
        />
        {unarchivedProperty.id && (
          <UnarchivePropertyModal
            open={unarchivePropertyModalOpen}
            propertyId={unarchivedProperty.id}
            propertyName={unarchivedProperty.name}
            propertyType={unarchivedPropertyType}
            reloadData={fetchProperties}
            close={closeUnarchivePropertyModal}
          />
        )}
      </div>
      {isLoadingPaginatedProperties && loadingPosition === 'bottom' && (
        <div
          data-testid="bottom-loading"
          className="bg-neutral-100 border-t border-b border-neutral-20 py-4 flex items-center justify-center gap-2"
        >
          <div className="w-30px">
            <LoadingSpinner message="" size={25} inline label="Loading farms" />
          </div>
          <p className="test-sm">Loading farms...</p>
        </div>
      )}
    </>
  );
};

DefaultTree.propTypes = {
  properties: PropTypes.arrayOf(PropTypes.object),
  propertiesFilter: PropTypes.func.isRequired,
  selectedPropertyId: PropTypes.string,
  expandedPropertyIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  loggedInUserOrgPermission: PropTypes.shape({
    role: PropTypes.string
  }).isRequired,
  searchTextValueFFT: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  dispatch: PropTypes.func.isRequired
};

export default memo(DefaultTree);
